import type { Variant } from '../gameTypes.js';
import type { PianoChart } from '../pianoChart.js';
import { PALETTE_MAGENTA } from '../builtinPulseMaps/palettes.js';
import { buildBuiltinAudio } from '../builtinPulseMaps/audioFromOnsets.js';
import audioOnsets from '../circlesBuiltinMaps/audio/on-and-on.onsets.json' with { type: 'json' };
import { pianoVariantsFromSpec } from './pianoFromOnsets.js';

/**
 * On & On — Cartoon ft. Daniel Levi (NCS). The "first piano lesson"
 * track in the catalogue: a slow 87 BPM means tiles cruise toward the
 * hit line, giving newcomers time to read each lane comfortably.
 *
 * Synthesised from the existing Circles onset analysis. To upgrade
 * with a curated piano-arrangement MIDI, drop the source MIDI into
 * `tools/piano-midi/on-and-on.mid`, fill in the manifest entry in
 * `tools/pianoMidiManifest.ts`, run
 * `tsx tools/ImportMidiToPiano.ts on-and-on`, and replace the
 * `pianoVariantsFromSpec(...)` call below with
 * `assembleImportedPianoChart(...)` once that helper exists.
 */
export const ON_AND_ON_PIANO_VARIANTS: Record<Variant, PianoChart> = pianoVariantsFromSpec({
  baseSlug: 'on-and-on',
  baseTitle: 'On & On',
  description:
    "Easy intro track. Cartoon's vocal-led electro-pop, charted as a flowing 4-lane melody.",
  difficulty: 1,
  totalBeats: 90,
  palette: PALETTE_MAGENTA,
  audio: buildBuiltinAudio({
    src: '/audio/circles/on-and-on.mp3',
    attribution:
      '"On & On" by Cartoon (feat. Daniel Levi) [NCS Release] - https://ncs.io/onandon',
    data: audioOnsets,
  }),
  onsets: audioOnsets,
  // One section: the whole song behaves the same melodically. The
  // generator's snake-pattern + intensity filter handle phrasing
  // implicitly via the intensity curve in the onset file.
  sections: [
    {
      startBeat: 0,
      endBeat: 90,
      selectOpts: { kinds: ['lead', 'snare'], minIntensity: 0.05, minSpacing: 0.5 },
    },
  ],
});
